/** Presentation helpers (pure, unit-testable). */

/** Same-origin URL for an R2 media key served by the platform Worker's /media route. */
export function mediaUrl(key: string | null | undefined): string | null {
  if (!key) return null;
  const trimmed = key.replace(/^\/+/, '');
  return trimmed.length > 0 ? `/media/${trimmed}` : null;
}

/** "3:12 PM" or "2 days ago" style relative time. */
export function relativeTime(instant: string | number | Date, now: Date = new Date()): string {
  const date = instant instanceof Date ? instant : new Date(instant);
  if (Number.isNaN(date.getTime())) return 'unknown';
  const diffMs = now.getTime() - date.getTime();
  const future = diffMs < 0;
  const seconds = Math.round(Math.abs(diffMs) / 1000);

  if (seconds < 45) return future ? 'in a moment' : 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return future ? `in ${minutes} min` : `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return future ? `in ${hours} h` : `${hours} h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return future ? `in ${days} d` : `${days} d ago`;
  return formatDate(date);
}

/** "Sep 25, 2026" */
export function formatDate(value: string | number | Date): string {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return 'unknown';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
}

/** "yyyy-mm-dd" for a local calendar day. */
export function toDateInputValue(value: string | number | Date): string {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Last N local days as 'YYYY-MM-DD', oldest first. */
export function lastNDates(days: number, now: Date = new Date()): string[] {
  const result: string[] = [];
  for (let offset = days - 1; offset >= 0; offset -= 1) {
    const date = new Date(now);
    date.setDate(date.getDate() - offset);
    result.push(toDateInputValue(date));
  }
  return result;
}

/** A printer is online if it heart-beated within the offline threshold. */
export function isPrinterOnline(
  lastSeenAt: string | null | undefined,
  now: Date = new Date(),
  thresholdMinutes = 2,
): boolean {
  if (!lastSeenAt) return false;
  const seen = new Date(lastSeenAt).getTime();
  if (Number.isNaN(seen)) return false;
  return now.getTime() - seen <= thresholdMinutes * 60_000;
}

/** Title-cases a slug or status like "awaiting_payment" → "Awaiting payment". */
export function humanize(value: string): string {
  const spaced = value.replace(/[_-]+/g, ' ').trim();
  if (spaced.length === 0) return value;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export function pluralize(count: number, singular: string, plural = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

/**
 * Human label for an audit-log actor. A null id is the system; a real actor
 * with no profile name falls back to a short account id instead of "System".
 */
export function activityActorLabel(
  actorUserId: string | null | undefined,
  fullName: string | null | undefined,
): string {
  if (!actorUserId) return 'System';
  const name = fullName?.trim();
  if (name) return name;
  return `Account ${actorUserId.slice(0, 8)}`;
}
